import { Document, IAttachment, IOrganization, IUser, IWorkspace } from "../../../@core/models/model";
import { AGENT_MODE } from "./types";

export enum LLM_PROVIDERS {
    OPENAI = "OPENAI",
    ANTHROPIC = "ANTHROPIC",
    GOOGLE = "GOOGLE",
    XAI = "XAI"
}

/** Default LLM provider used across the app (e.g. model selector, chat input). */
export const DEFAULT_LLM_PROVIDER = LLM_PROVIDERS.ANTHROPIC;

export enum THREAD_TYPE {
    GENERAL_AGENT = 'GENERAL_AGENT',
    DOCUMENT_CHAT = 'DOCUMENT_CHAT',
    COMPARE_CHAT = 'COMPARE_CHAT',
    QNA_CHAT = 'QNA_CHAT',
    /** Chat type scoped to a geotechnical analysis report; documentId carries the analysis ID */
    GEOTECHNICAL_CHAT = 'GEOTECHNICAL_CHAT',
}

export enum THREAD_PARENT_TYPE {
    GENERAL_AGENT = 'GENERAL_AGENT'
}

export enum THREAD_CHILD_TYPE {
    CHAT = 'Conversations'
}

export type ToolStatus = 'loading' | 'success' | 'failure' | 'STREAMING_STARTED' | 'COMPLETED';

export enum THREAD_METADATA_STATUS {
    INACTIVE = 'INACTIVE',
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
    INTERRUPTED = 'INTERRUPTED',
    PAUSED = 'PAUSED',
    CANCELLED = 'CANCELLED',
}

/**
 * Statuses published via RabbitMQ for real-time streaming.
 * Extends THREAD_METADATA_STATUS with streaming-only statuses that are never saved to MongoDB.
 * Values that overlap with THREAD_METADATA_STATUS must stay in sync.
 */
export enum THREAD_STREAMING_STATUS {
    // Metadata statuses (shared with MongoDB) — keep in sync with THREAD_METADATA_STATUS
    INACTIVE = 'INACTIVE',
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
    PAUSED = 'PAUSED',
    CANCELLED = 'CANCELLED',
    INTERRUPTED = 'INTERRUPTED',

    // Streaming-only statuses (never saved to MongoDB)
    STREAMING_STARTED = 'STREAMING_STARTED',
    SUMMARIZING = 'SUMMARIZING',
    SUMMARIZATION_COMPLETE = 'SUMMARIZATION_COMPLETE',
    SUMMARIZATION_FAILED = 'SUMMARIZATION_FAILED',

    // Tool execution statuses (streaming-only)
    TOOL_ARG_STREAMING = 'TOOL_ARG_STREAMING',
}


export interface IThread extends Document {
    organization: string | IOrganization,
    workspace: string | IWorkspace,


    type: THREAD_TYPE,
    parent: string,
    parentType: THREAD_PARENT_TYPE,
    child: string,
    childType: THREAD_CHILD_TYPE,

    title: string,
    sandbox: string, // sandbox id
    backgroundTask: string, // background task id

    createdBy: string | IUser,
    createdAt: Date,
    updatedAt: Date
}

export interface IThreadMetadata extends Document {
    organization: string | IOrganization,
    workspace: string | IWorkspace,

    thread: string | IThread,
    langChainThread: string,
    attachments: string[] | IAttachment[],
    status?: THREAD_METADATA_STATUS,
    errorMessage?: string,
    inputTokens?: number,

    // conversationTokens: string |  ITokenUsage,
    // summarizationTokens: string |  ITokenUsage,

    createdBy: string | IUser,
    provider: LLM_PROVIDERS,
    toolParams?: Record<string, any>[],

    createdAt: Date,
    updatedAt: Date

    connectors: string[],
    interruptedToolNames: string[],
    totalTurnTokens?: number,
    pauseReason?: string | null,
    draftPrompt?: string,
    activeMode?: AGENT_MODE
}

export type ITokenLimitsAgent = Record<LLM_PROVIDERS, { max_tokens: number, max_requests: number, batch_limit?: number }>

export const default_token_limits_agent = {
    [LLM_PROVIDERS.OPENAI]: {
        max_tokens: 1000000,
        max_requests: 25,
    },
    [LLM_PROVIDERS.ANTHROPIC]: {
        max_tokens: 200000,
        max_requests: 1000,
    },
    [LLM_PROVIDERS.GOOGLE]: {
        max_tokens: 1048576,
        max_requests: 1000,
    },
    [LLM_PROVIDERS.XAI]: {
        max_tokens: 2000000,
        max_requests: 480,
    },
}

export interface MessageItem {
    tool_calls?: any[];
    content: string;
    type: MESSAGE_TYPE;
    name?: string;
    id: string;
    additional_kwargs?: any;
    artifact?: any;
    status?: string;
    tool_call_id?: string;
    temp_id?: string;
    [key: string]: any;
}

export enum MESSAGE_TYPE {
    HUMAN = 'human',
    AI = 'ai',
    TOOL = 'tool',
    SYSTEM = 'system',
    /** Synthetic message injected client-side to display an approved enhanced prompt as a read-only document card. */
    ENHANCED_PROMPT = 'enhanced_prompt',
}
